import { DarkModeSwitch } from '@repo/studio-components/top-app-bar/components/dark-mode-switch'
import { m } from 'framer-motion'
import { NsfwPlatformHidden } from '@repo/studio-config/nsfw-platform-hidden'
import { useStudioNavigationConfig } from '@repo/studio-config/studio-config-context'
import type { NavigationFooterItem } from '@repo/studio-config/studio-config-context'
import { ApiPlatformLink } from './api-platform-link'
import { CharactersLink } from './characters-link'
import { GatedEllipsisMenu } from './ellipsis-menu'
import { LanguageSwitcher } from './language-switcher'
import { McpLink } from './mcp-link'

export function NavigationFooter({
  items,
  expanded,
}: {
  items: NavigationFooterItem[]
  expanded: boolean
}) {
  const Footer = useStudioNavigationConfig()?.Footer
  return Footer ? (
    <Footer expanded={expanded} />
  ) : (
    <DefaultNavigationFooter items={items} expanded={expanded} />
  )
}

function DefaultNavigationFooter({
  items,
  expanded,
}: {
  items: NavigationFooterItem[]
  expanded: boolean
}) {
  return items.map(item => {
    switch (item.kind) {
      case 'api-platform': {
        return (
          <m.div key={item.kind} layoutId="sidebar-api">
            <ApiPlatformLink />
          </m.div>
        )
      }
      case 'mcp': {
        return (
          <m.div key={item.kind} layoutId="sidebar-mcp">
            <McpLink href={item.href} />
          </m.div>
        )
      }
      case 'characters': {
        return (
          <NsfwPlatformHidden key={item.kind}>
            <m.div layoutId="sidebar-characters">
              <CharactersLink route={item.route} />
            </m.div>
          </NsfwPlatformHidden>
        )
      }
      case 'dark-mode': {
        return (
          <m.div key={item.kind} layoutId="sidebar-darkmode">
            <DarkModeSwitch size="small" />
          </m.div>
        )
      }
      case 'language': {
        return (
          <m.div key={item.kind} layoutId="sidebar-lang">
            <LanguageSwitcher
              layout="horizontal"
              className={expanded ? 'left-[225px]' : 'left-[60px]'}
            />
          </m.div>
        )
      }
      case 'menu': {
        return (
          <m.div key={item.kind} layoutId="sidebar-menu">
            <GatedEllipsisMenu />
          </m.div>
        )
      }
      default: {
        const unhandled: never = item
        return unhandled
      }
    }
  })
}

export function StackedNavigationFooter({
  items,
  onNavigate,
}: {
  items: NavigationFooterItem[]
  onNavigate?: () => void
}) {
  const Footer = useStudioNavigationConfig()?.Footer
  return Footer ? (
    <Footer expanded onNavigate={onNavigate} />
  ) : (
    <DefaultStackedNavigationFooter items={items} onNavigate={onNavigate} />
  )
}

function DefaultStackedNavigationFooter({
  items,
  onNavigate,
}: {
  items: NavigationFooterItem[]
  onNavigate?: () => void
}) {
  return items.map(item => {
    switch (item.kind) {
      case 'api-platform': {
        return <ApiPlatformLink key={item.kind} />
      }
      case 'mcp': {
        return <McpLink key={item.kind} href={item.href} />
      }
      case 'characters': {
        return (
          <NsfwPlatformHidden key={item.kind}>
            <CharactersLink route={item.route} className="flex" />
          </NsfwPlatformHidden>
        )
      }
      case 'dark-mode': {
        return <DarkModeSwitch key={item.kind} className="flex" size="small" />
      }
      case 'language': {
        return <LanguageSwitcher key={item.kind} layout="vertical" />
      }
      case 'menu': {
        return <GatedEllipsisMenu key={item.kind} onNavigate={onNavigate} />
      }
      default: {
        const unhandled: never = item
        return unhandled
      }
    }
  })
}
